import { useTheme, SectionConfig } from "@/contexts/ThemeContext";

function renderFrontendBlock(section: SectionConfig, content: Record<string, any>) {
  const bt = section.blockType;

  if (bt === 'spacer') return <div key={section.id} className="py-12 bg-background" />;

  if (bt === 'text') {
    return (
      <section key={section.id} className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-serif text-3xl font-semibold mb-4 text-foreground">{content.heading || 'Section Title'}</h2>
          <p className="text-lg leading-relaxed text-muted-foreground">{content.description || 'Your content goes here.'}</p>
        </div>
      </section>
    );
  }

  if (bt === 'image-text') {
    return (
      <section key={section.id} className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted">
            {content.imageUrl && <img src={content.imageUrl} alt="" className="w-full h-full object-cover" />}
          </div>
          <div>
            <h2 className="font-serif text-3xl font-semibold mb-4 text-foreground">{content.heading || 'About Us'}</h2>
            <p className="text-lg mb-6 text-muted-foreground">{content.description || 'Tell your story here.'}</p>
            {content.buttonText && (
              <a href={content.buttonLink || '#'} className="inline-block px-6 py-2.5 rounded font-semibold bg-accent text-accent-foreground">{content.buttonText}</a>
            )}
          </div>
        </div>
      </section>
    );
  }

  if (bt === 'cards') {
    const items = content.items || [{ title: 'Feature 1', description: 'Desc' }, { title: 'Feature 2', description: 'Desc' }, { title: 'Feature 3', description: 'Desc' }];
    return (
      <section key={section.id} className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl font-semibold mb-3 text-foreground">{content.heading || 'Features'}</h2>
            <p className="text-muted-foreground">{content.description || 'Explore what we offer'}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {items.map((item: any, i: number) => (
              <div key={i} className="p-6 rounded-lg shadow-sm bg-card">
                <h3 className="text-lg font-semibold mb-2 text-foreground">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (bt === 'cta' || bt === 'banner') {
    return (
      <section key={section.id} className="py-16 bg-accent text-accent-foreground">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl font-semibold mb-4">{content.heading || 'Ready?'}</h2>
          <p className="text-lg opacity-80 mb-8">{content.description || 'Take action now.'}</p>
          {content.buttonText && (
            <a href={content.buttonLink || '#'} className="inline-block px-8 py-3 rounded font-semibold bg-primary text-primary-foreground">{content.buttonText}</a>
          )}
        </div>
      </section>
    );
  }

  if (bt === 'testimonial') {
    return (
      <section key={section.id} className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl font-semibold mb-10 text-foreground">{content.heading || 'What Our Clients Say'}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="p-6 rounded-lg bg-card">
                <p className="text-sm italic mb-4 text-muted-foreground">"Amazing experience working with this team."</p>
                <div className="h-3 w-24 mx-auto rounded bg-muted" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (bt === 'faq') {
    const items = content.items || [{ title: 'Question?', description: 'Answer.' }];
    return (
      <section key={section.id} className="py-16 bg-background">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-serif text-3xl font-semibold mb-8 text-center text-foreground">{content.heading || 'FAQ'}</h2>
          {items.map((item: any, i: number) => (
            <div key={i} className="border-b border-border py-4">
              <h3 className="font-semibold mb-1 text-foreground">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.description}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (bt === 'gallery') {
    return (
      <section key={section.id} className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-serif text-3xl font-semibold mb-8 text-center text-foreground">{content.heading || 'Gallery'}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="aspect-square rounded-lg bg-muted" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (bt === 'hero') {
    return (
      <section key={section.id} className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl font-semibold mb-4">{content.heading || section.name}</h2>
          <p className="text-lg opacity-70 max-w-2xl mx-auto mb-8">{content.description}</p>
          {content.buttonText && (
            <a href={content.buttonLink || '#'} className="inline-block px-8 py-3 rounded font-semibold bg-accent text-accent-foreground">{content.buttonText}</a>
          )}
        </div>
      </section>
    );
  }

  return (
    <section key={section.id} className="py-16 bg-muted">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="font-serif text-2xl font-semibold text-foreground">{content.heading || section.name}</h2>
        <p className="mt-2 text-muted-foreground">{content.description || 'Custom section'}</p>
      </div>
    </section>
  );
}

export default function CustomSectionsRenderer({ pageId }: { pageId: string }) {
  const { themeState } = useTheme();
  const published = themeState.published;
  const customSections = published.customSections?.[pageId] || [];

  if (customSections.length === 0) return null;

  return (
    <>
      {customSections
        .filter(s => s.visible)
        .sort((a, b) => a.order - b.order)
        .map(section => {
          const content = published.pageContent?.[pageId]?.[section.id] || {};
          return renderFrontendBlock(section, content);
        })}
    </>
  );
}
